/**
 * AnimatedCard Component
 *
 * Wrapper component that adds Framer Motion entry/exit animations to cards.
 * Used for staggered grid animations in Content Hub.
 */

import { motion } from "framer-motion";
import PropTypes from "prop-types";

// Animation variants for card entry
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      delay: custom * 0.05, // Stagger based on index
      ease: [0.25, 0.1, 0.25, 1], // Custom easing
    },
  }),
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: {
      duration: 0.25,
    },
  },
};

// Hover animation variants
const hoverVariants = {
  rest: {
    scale: 1,
  },
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
  tap: {
    scale: 0.98,
  },
};

function AnimatedCard({ children, index = 0, enableHover = false, layoutId }) {
  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      whileHover={enableHover ? "hover" : undefined}
      whileTap={enableHover ? "tap" : undefined}
      layoutId={layoutId}
      style={{
        height: "100%",
      }}
    >
      {children}
    </motion.div>
  );
}

AnimatedCard.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number,
  enableHover: PropTypes.bool,
  layoutId: PropTypes.string,
};

export default AnimatedCard;

/**
 * AnimatedGrid Component
 *
 * Container for animated card grids with AnimatePresence support.
 */
export function AnimatedGrid({ children }) {
  return (
    <motion.div initial="hidden" animate="visible" exit="exit" style={{ display: "contents" }}>
      {children}
    </motion.div>
  );
}

AnimatedGrid.propTypes = {
  children: PropTypes.node,
};
